import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';

export default function Layout({ children }) {
  const { admin, logout, isSuperadmin, isPartner } = useAuth();
  const { theme, toggleTheme, lang, setLang, t } = useSettings();
  const navigate  = useNavigate();
  const location  = useLocation();

  const [collapsed,  setCollapsed]  = useState(() => localStorage.getItem('sidebarCollapsed') === 'true');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile,   setIsMobile]   = useState(window.innerWidth <= 768);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    if (!isPartner) {
      axios.get('/api/requests/admin-unread').then(r => setPendingCount(r.data.count)).catch(() => {});
      const iv = setInterval(() => {
        axios.get('/api/requests/admin-unread').then(r => setPendingCount(r.data.count)).catch(() => {});
      }, 30000);
      return () => clearInterval(iv);
    }
  }, [isPartner]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('sidebarCollapsed', String(next));
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  const navItems = [
    { to: '/dashboard',          label: t('restaurants'),      icon: '◫', exact: false },
    { to: '/audit-logs',         label: t('auditLogs'),        icon: '◎' },
    { to: '/admin-requests',     label: t('requests'),         icon: '◈', badge: pendingCount },
    ...(isSuperadmin ? [{ to: '/admin-management', label: t('adminManagement'), icon: '◉' }] : []),
    ...(isSuperadmin ? [{ to: '/plans', label: t('planManagement'), icon: '◆' }] : []),
  ];

  const isCollapsed = !isMobile && collapsed;
  const showLabels  = isMobile || !collapsed;

  const SidebarContent = () => (
    <>
      {/* Logo row */}
      <div style={{
        display: 'flex',
        flexDirection: isCollapsed ? 'column' : 'row',
        alignItems: 'center',
        padding: isCollapsed ? '12px 10px 8px' : '16px 16px 16px',
        gap: isCollapsed ? 6 : 0,
        justifyContent: isCollapsed ? 'center' : 'space-between',
        transition: 'padding 0.25s',
      }}>
        {/* Logo — capped width when collapsed so it never overflows the 60px rail */}
        <img
          src="/logo.png"
          alt="Inone"
          style={{
            height: isCollapsed ? 28 : 44,
            maxWidth: isCollapsed ? 40 : 160,
            width: 'auto',
            objectFit: 'contain',
            flexShrink: 0,
            transition: 'height 0.25s, max-width 0.25s',
          }}
        />
        {!isMobile && (
          <button className="sidebar-collapse-btn" onClick={toggleCollapsed} title={collapsed ? 'Expand' : 'Collapse'}
            style={{ flexShrink: 0 }}>
            {collapsed ? '»' : '«'}
          </button>
        )}
        {isMobile && (
          <button onClick={() => setMobileOpen(false)} style={{
            background: 'none', border: 'none', color: 'var(--text-muted)',
            fontSize: 22, lineHeight: 1, padding: '0 4px', marginLeft: 'auto',
          }}>×</button>
        )}
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: '0 10px' }}>
        {navItems.map(item => (
          <NavLink key={item.to} to={item.to} end={item.exact}
            title={isCollapsed ? item.label : undefined}
            style={({ isActive }) => ({
              display: 'flex', alignItems: 'center',
              gap: showLabels ? 10 : 0,
              justifyContent: isCollapsed ? 'center' : 'flex-start',
              padding: '10px 12px', borderRadius: 'var(--radius-sm)',
              color: isActive ? 'var(--accent)' : 'var(--text-muted)',
              background: isActive ? 'var(--accent-glow)' : 'transparent',
              fontSize: 14, fontWeight: isActive ? 500 : 400,
              marginBottom: 4, transition: 'all 0.15s', overflow: 'hidden',
            })}>
            <span style={{ fontSize: 18, flexShrink: 0 }}>{item.icon}</span>
            {showLabels && <span className="sidebar-label">{item.label}</span>}
            {showLabels && item.badge > 0 && (
              <span style={{ marginLeft: 'auto', background: 'var(--red)', color: '#fff', borderRadius: 10, fontSize: 11, fontWeight: 700, padding: '1px 7px', flexShrink: 0 }}>{item.badge}</span>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Bottom */}
      <div style={{
        borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 16,
        padding: isCollapsed ? '16px 10px 12px' : '16px 14px 12px',
      }}>
        {showLabels ? (
          <div className="sidebar-bottom-full">
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: 'var(--text-dim)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 7, paddingLeft: 2 }}>Theme</div>
              <div className="toggle-pill">
                <button className={theme === 'dark'  ? 'active' : ''} onClick={() => theme !== 'dark'  && toggleTheme()}>🌙 {t('darkMode')}</button>
                <button className={theme === 'light' ? 'active' : ''} onClick={() => theme !== 'light' && toggleTheme()}>☀️ {t('lightMode')}</button>
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: 'var(--text-dim)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 7, paddingLeft: 2 }}>Language</div>
              <div className="toggle-pill">
                {[['en','EN'],['ru','RU'],['uz','UZ']].map(([code, label]) => (
                  <button key={code} className={lang === code ? 'active' : ''} onClick={() => setLang(code)}>{label}</button>
                ))}
              </div>
            </div>
            <div style={{ background: 'var(--surface2)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 2 }}>{t('signedInAs')}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{admin?.username}</div>
                {isSuperadmin && (
                  <span style={{ fontSize: 10, background: 'var(--accent-glow)', color: 'var(--accent)', padding: '2px 7px', borderRadius: 10, fontWeight: 600, flexShrink: 0 }}>SA</span>
                )}
              </div>
            </div>
            <button onClick={handleLogout} className="btn-ghost" style={{ width: '100%', textAlign: 'left', fontSize: 13, marginBottom: 4 }}>
              ↪ {t('signOut')}
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
            <button onClick={toggleTheme} title={theme === 'dark' ? t('lightMode') : t('darkMode')}
              style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, width: 36, height: 36, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>
            <button onClick={handleLogout} title={t('signOut')}
              style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, width: 36, height: 36, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
              ↪
            </button>
          </div>
        )}
      </div>
    </>
  );

  return (
    <>
      {/* Mobile topbar */}
      <header className="mobile-topbar">
        <button className="hamburger" onClick={() => setMobileOpen(true)} aria-label="Open menu">
          <span /><span /><span />
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <img src="/logo.png" alt="Inone" style={{ width: 28, height: 28, objectFit: 'contain' }} />
          <span style={{ fontWeight: 700, fontSize: 16, color: 'var(--text)' }}>{t('appName')}</span>
        </div>
        <button onClick={toggleTheme} style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, width: 36, height: 36, fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }} aria-label="Toggle theme">
          {theme === 'dark' ? '☀️' : '🌙'}
        </button>
      </header>

      {mobileOpen && <div className="sidebar-overlay" onClick={() => setMobileOpen(false)} />}

      <div className="layout-shell">
        <aside className={['sidebar', isCollapsed ? 'collapsed' : '', mobileOpen ? 'mobile-open' : ''].join(' ')}>
          <SidebarContent />
        </aside>
        <main className="layout-main">{children}</main>
      </div>
    </>
  );
}
